import React from "react";

import type { CoverageCell, TelemetrySummary } from "@/lib/telemetry";
import { formatNumber, formatTs, type TelemetryTz } from "@/components/telemetry/format";

function Cell({
  label,
  value,
  hint,
  tone,
  testId,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: "warn" | "error";
  testId?: string;
}) {
  const toneClass =
    tone === "error" ? "text-destructive" : tone === "warn" ? "text-amber-600 dark:text-amber-400" : "";
  return (
    <div className="rounded-lg border border-border px-3 py-2" data-testid={testId}>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className={`mt-0.5 truncate text-sm font-semibold ${toneClass}`}>{value}</div>
      {hint && <div className="truncate text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

/**
 * The counters, and the honesty about which number is which.
 *
 * "Last archived" is not "last seen": the lake lags, so labelling the newest
 * archived sample as a liveness signal would make a lagging feeder look like a
 * dead charger. Wide windows have no server-side summary, so this falls back to
 * what coverage can actually total and says the events counts are page-scoped.
 */
export function SummaryStrip({
  summary,
  coverage,
  eventCount,
  tz,
}: {
  summary: TelemetrySummary | null;
  coverage: CoverageCell[];
  eventCount: number;
  tz: TelemetryTz;
}) {
  const withData = coverage.filter((c) => c.samples > 0).length;
  const notArchived = coverage.filter((c) => c.status === "not_archived").length;
  const granularity = coverage.some((c) => c.hour !== undefined) ? "h" : "d";

  if (!summary) {
    const samples = coverage.reduce((sum, c) => sum + c.samples, 0);
    const commands = coverage.reduce((sum, c) => sum + c.commands, 0);
    const errors = coverage.reduce((sum, c) => sum + c.errors, 0);
    return (
      <div
        className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5"
        data-testid="telemetry-summary"
      >
        <Cell label="Samples archived" value={samples.toLocaleString()} />
        <Cell
          label="Coverage"
          value={`${withData}/${coverage.length}${granularity}`}
          hint={notArchived ? `${notArchived} not archived yet` : undefined}
        />
        <Cell label="Commands" value={commands.toLocaleString()} />
        <Cell
          label="Error samples"
          value={errors.toLocaleString()}
          tone={errors ? "error" : undefined}
        />
        <Cell label="Events loaded" value={eventCount.toLocaleString()} hint="this page only" />
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6"
      data-testid="telemetry-summary"
    >
      <Cell
        label="Last archived"
        value={formatTs(summary.last_archived_ts, tz)}
        hint={summary.last_state ? `state: ${summary.last_state}` : undefined}
        testId="telemetry-last-archived"
      />
      <Cell
        label="Samples"
        value={summary.samples.toLocaleString()}
        hint={
          summary.last_voltage != null || summary.last_current != null
            ? `last ${formatNumber(summary.last_voltage)} V / ${formatNumber(summary.last_current)} A`
            : undefined
        }
      />
      <Cell
        label="Coverage"
        value={`${withData}/${coverage.length}${granularity}`}
        hint={notArchived ? `${notArchived} not archived yet` : undefined}
      />
      <Cell label="State changes" value={summary.state_changes.toLocaleString()} />
      <Cell
        label="Gaps"
        value={summary.gaps.toLocaleString()}
        tone={summary.gaps ? "warn" : undefined}
      />
      <Cell
        label="Anomalies"
        value={summary.anomalies.toLocaleString()}
        hint={summary.commands ? `${summary.commands} commands` : undefined}
        tone={summary.anomalies_error ? "error" : summary.anomalies ? "warn" : undefined}
      />
    </div>
  );
}
